// The calls screens put together for cloud's host (main.tsx):
// - the Workspace section Calls (60) and its routes (the list, a new call, a
//   call), and the team events of calls it hears;
// - the Me part: the others' voices played outside every screen (Background)
//   and the call I am in at the foot of every sidebar (Ongoing).
//
// Promise: one call session per call of this function (per app), never a
// module's; the section and the Me part share it.
import { createRoute, Outlet } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import {
  defineScreenPart,
  iconOf,
  lazyScreen,
  type OngoingProps,
  type ScreenPart,
  withLazy,
} from "@trameria/ui-core";
import { defineWorkspaceSection, type WorkspaceSection } from "../workspace";
import { VoiceAudio } from "./audio";
import { createVoiceSession, type VoiceSession } from "./lib/session";
import { JoinedCall, useJoinedCallData } from "./ongoing";
import { voiceTeamHandlers } from "./realtime";

// The screens are loaded when first opened (ui-core's app/lazy.tsx); the
// session, its sound and its row are not (they live outside every screen).
const CallsScreen = lazyScreen(() => import("./list"), "CallsScreen");
const NewCall = lazyScreen(() => import("./new"), "NewCall");
const RoomScreen = lazyScreen(() => import("./room"), "RoomScreen");

export interface CallsScreens {
  section: WorkspaceSection;
  /** For `createMeApp({ parts })`. */
  mePart: ScreenPart;
  session: VoiceSession;
}

export function callsScreen(session = createVoiceSession()): CallsScreens {
  const section = defineWorkspaceSection({
    id: "calls",
    // Under Topics: the workspace's talk together (the person, 2026-10-30).
    order: 35,
    label: () => m.vc_calls(),
    icon: iconOf("voice"),
    to: "/calls",
    feature: "voice",
    routes: (ws) => {
      const list = createRoute({
        getParentRoute: () => ws,
        path: "calls",
        component: withLazy(function CallsRoute() {
          return (
            <CallsScreen>
              <Outlet />
            </CallsScreen>
          );
        }, CallsScreen),
      });
      return [
        list.addChildren([
          // Nothing open: the list is alone and no detail is drawn.
          createRoute({ getParentRoute: () => list, path: "/" }),
          createRoute({
            getParentRoute: () => list,
            path: "new",
            component: NewCall,
          }),
          createRoute({
            getParentRoute: () => list,
            path: "$roomId",
            component: withLazy(function CallRoute() {
              return <RoomScreen session={session} />;
            }, RoomScreen),
          }),
        ]),
      ];
    },
    realtime: voiceTeamHandlers,
  });

  const mePart = defineScreenPart({
    id: "calls",
    Background: function CallAudio() {
      return <VoiceAudio session={session} />;
    },
    Ongoing: function Ongoing(props: OngoingProps) {
      return <JoinedCall session={session} {...props} />;
    },
    useOngoingData: () => useJoinedCallData(session),
  });

  return { section, mePart, session };
}
